import { useState, useRef, useEffect } from "react";
import type { PlayerGameData } from "../types";
import type { PlayerLobbyData } from "../../../store";
import type { ThemeColors } from "../../../theme";
import { Button } from "../../../../components/ui/Button";
import { ProviderBadge } from "../../../../components/ProviderBadge";
import { ProfileAvatar } from "../../../../components/ProfileAvatar";
import { trackEvent } from "../../../analytics";
import { useTranslation } from "react-i18next";

interface PlayerListProps {
  gamePlayers: Record<string, PlayerGameData> | null;
  lobbyPlayers: Record<string, PlayerLobbyData>;
  showLobbyPoints: boolean;
  currentPlayerId?: string;
  theme: ThemeColors;
  isDark: boolean;
  hostId: string;
  onKickPlayer: (playerId: string) => void;
}

export function PlayerList({
  lobbyPlayers,
  gamePlayers,
  showLobbyPoints,
  currentPlayerId,
  theme,
  isDark,
  hostId,
  onKickPlayer,
}: PlayerListProps) {
  const [selectedPlayer, setSelectedPlayer] = useState<{
    id: string;
    name: string;
    top: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isHost = currentPlayerId === hostId;
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSelectedPlayer(null);
      }
    };
    if (selectedPlayer) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedPlayer]);

  const handlePlayerClick = (
    playerId: string,
    playerName: string | undefined,
  ) => {
    const rowEl = rowRefs.current[playerId];
    const containerEl = containerRef.current;

    if (selectedPlayer?.id === playerId) {
      setSelectedPlayer(null);
    } else if (rowEl && containerEl) {
      const containerRect = containerEl.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      const top = rowRect.top - containerRect.top + rowRect.height / 2;
      setSelectedPlayer({
        id: playerId,
        name: playerName || t("components.playerList.unknown"),
        top,
      });
    }
  };

  const handleKickPlayer = (playerId: string, playerName: string) => {
    trackEvent("kick-player-execute", { playerName });
    onKickPlayer(playerId);
    setSelectedPlayer(null);
  };

  const allPlayerIds = new Set([
    ...Object.keys(lobbyPlayers),
    ...(gamePlayers ? Object.keys(gamePlayers) : []),
  ]);

  const showGamePoints = gamePlayers !== null;
  const showBothPoints = showLobbyPoints && showGamePoints;

  const playerEntries = Array.from(allPlayerIds).sort((a, b) => {
    const aPoints = showBothPoints
      ? (gamePlayers?.[a]?.points ?? 0)
      : showGamePoints
        ? (gamePlayers?.[a]?.points ?? 0)
        : (lobbyPlayers[a]?.points ?? 0);
    const bPoints = showBothPoints
      ? (gamePlayers?.[b]?.points ?? 0)
      : showGamePoints
        ? (gamePlayers?.[b]?.points ?? 0)
        : (lobbyPlayers[b]?.points ?? 0);
    return bPoints - aPoints;
  });

  return (
    <div
      ref={containerRef}
      onMouseLeave={() => setSelectedPlayer(null)}
      className={`${theme.sectionBg} ${theme.sectionBorder} sm:rounded-xl sm:p-4 w-full h-full flex flex-col relative`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-2 mb-4 pb-3 border-b ${isDark ? "border-zinc-800/50" : "border-gray-200"}`}
      >
        <svg
          className={`w-4 h-4 ${isDark ? "text-zinc-400" : "text-gray-500"}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <span
          className={`text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
        >
          {t("components.playerList.playersWithCount", {
            count: playerEntries.length,
          })}
        </span>
      </div>

      {/* Player List */}
      <div className="flex-1 space-y-1 overflow-auto">
        {playerEntries.map((playerId, index) => {
          const playerName = lobbyPlayers[playerId]?.name;
          const gamePoints = gamePlayers?.[playerId]?.points;
          const lobbyPoints = lobbyPlayers[playerId]?.points;
          const isCurrentPlayer = playerId === currentPlayerId;
          const displayPoints = showGamePoints ? gamePoints : lobbyPoints;
          const isSelected = selectedPlayer?.id === playerId;

          return (
            <div
              key={playerId}
              ref={(el) => {
                rowRefs.current[playerId] = el;
              }}
              onClick={() => handlePlayerClick(playerId, playerName)}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                isSelected
                  ? isDark
                    ? "bg-zinc-800 border border-zinc-700"
                    : "bg-gray-100 border border-gray-200"
                  : isCurrentPlayer
                    ? isDark
                      ? "bg-zinc-800/50 border border-zinc-700/50"
                      : "bg-gray-50 border border-gray-200/50"
                    : isDark
                      ? "hover:bg-zinc-900/50"
                      : "hover:bg-gray-100"
              }`}
            >
              <div className="flex w-5 shrink-0 items-start pt-0.5">
                <span className={`w-5 font-mono text-xs ${theme.textMuted}`}>
                  {index + 1}
                </span>
              </div>
              <div
                className="flex min-w-0 flex-1 flex-col items-start"
                data-testid="player-identity"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <ProfileAvatar
                    image={lobbyPlayers[playerId]?.image}
                    name={playerName || "Player"}
                    size="sm"
                  />
                  <span
                    className={`block max-w-full truncate text-sm ${isCurrentPlayer ? "font-medium" : ""} ${theme.text}`}
                    data-testid="player-name"
                  >
                    {playerName?.slice(0, 15)}
                  </span>
                </div>
                {((lobbyPlayers[playerId]?.provider !== "username" &&
                  Boolean(lobbyPlayers[playerId]?.provider)) ||
                  isCurrentPlayer) && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <ProviderBadge
                      provider={
                        lobbyPlayers[playerId]?.provider === "username"
                          ? null
                          : lobbyPlayers[playerId]?.provider
                      }
                    />
                    {isCurrentPlayer && (
                      <span
                        className={`rounded-md border px-1.5 py-0.5 text-[10px] leading-none font-medium ${theme.badge}`}
                      >
                        {t("components.playerList.you")}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {showBothPoints ? (
                <div className="flex items-center gap-3 text-xs">
                  <span className={theme.textMuted}>{lobbyPoints ?? "-"}</span>
                  <span className={`font-medium ${theme.text}`}>
                    {gamePoints ?? "-"}
                  </span>
                </div>
              ) : (
                <span
                  className={`text-sm font-medium tabular-nums ${theme.text}`}
                >
                  {displayPoints ?? "-"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with column labels if showing both */}
      {showBothPoints && (
        <div
          className={`flex items-center justify-end gap-3 mt-3 pt-3 border-t ${isDark ? "border-zinc-800/50" : "border-gray-200"} text-xs`}
        >
          <span className={theme.textMuted}>
            {t("components.playerList.lobby")}
          </span>
          <span className={theme.textMuted}>
            {t("components.playerList.game")}
          </span>
        </div>
      )}

      {/* Popover - to the right of the list */}
      {selectedPlayer && (
        // pl-2 in seperate div so that we have fake margin that does not trigger onMouseLeave
        <div
          className="absolute pl-2"
          style={{
            left: "100%",
            top: selectedPlayer.top,
            transform: "translateY(-50%)",
          }}
        >
          <div
            className={`p-3 rounded-lg border shadow-lg z-50 min-w-48 ${
              isDark
                ? "bg-zinc-900 border-zinc-700"
                : "bg-white border-gray-200 shadow-black/10"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${theme.text}`}>
                {selectedPlayer.name}
              </span>
            </div>
            <p className={`text-sm mb-3 ${theme.textMuted}`}>
              {t("components.playerList.playerOptions")}
            </p>
            {isHost && selectedPlayer.id !== currentPlayerId && (
              <div className="w-full flex justify-center">
                <Button
                  variant="primary"
                  isDark={isDark}
                  onClick={() =>
                    handleKickPlayer(selectedPlayer.id, selectedPlayer.name)
                  }
                  className="p-2 w-30"
                >
                  {t("components.playerList.kickPlayer")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
